import { HttpClientXHR } from "./config/http.client";
import { GenericHttpClient } from "./config/http.generic.client";

export interface CreateVoiceCallResponse {
  callId: string;
  /** WebRTC SDP answer for the offer the widget sent. */
  sdp: string;
  conversationShareToken: string;
  maxDurationSec: number;
}

export interface CreateVoiceCallArgs {
  sdp: string;
  conversationShareToken?: string;
  botId?: string;
  lang?: string;
}

const CREATE_CALL_TIMEOUT_MS = 30000;

/**
 * "Speak to Kai": the widget starts the call with its WebRTC offer, reports
 * when its audio plays (then the server greets) and when the customer hangs
 * up. Its data channel can only hang up: everything Kai says comes from the
 * server.
 */
export class HttpCallsService extends GenericHttpClient<any> {
  static _instance: HttpCallsService;
  static getInstance(): HttpCallsService {
    if (this._instance == null) {
      this._instance = new HttpCallsService("/v3/shared/calls");
    }
    return this._instance;
  }

  /**
   * Create the call: the WebRTC offer goes in, the session's answer comes
   * back. The server sets up the conversation and the provider session on
   * this request (a few seconds), so it gets a long timeout and NO retry: a
   * retried POST would start a second call that supersedes the first.
   */
  async startCall(args: CreateVoiceCallArgs): Promise<CreateVoiceCallResponse> {
    const response = await this.post<CreateVoiceCallResponse>(this.path, args, {
      timeout: CREATE_CALL_TIMEOUT_MS,
      retry: false,
    });
    return response.data;
  }

  /** The widget's audio plays: the server greets the customer. Idempotent, so retried like any POST. */
  async markReady(callId: string): Promise<any> {
    return this.post<any>(`${this.path}/${callId}/ready`, {});
  }

  async endCall(callId: string): Promise<any> {
    return this.post<any>(`${this.path}/${callId}/end`, {});
  }

  /**
   * pagehide-safe hang-up: XHR is aborted with the unloading document, fetch
   * keepalive survives it. Same auth headers the XHR client injects
   * (api-token / gleap-id / gleap-hash). Fire-and-forget by design.
   */
  endCallKeepalive(callId: string): void {
    try {
      if (typeof fetch !== "function") return;
      const headers = this.injectToken({
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      });
      const stringHeaders: Record<string, string> = {};
      Object.keys(headers).forEach((key) => {
        stringHeaders[key] = String(headers[key]);
      });
      fetch(`${HttpClientXHR.baseURL}${this.path}/${callId}/end`, {
        method: "POST",
        keepalive: true,
        headers: stringHeaders,
        body: "{}",
      }).catch(() => {});
    } catch (_) {}
  }
}
